import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import { useFedStore } from '../store';
import { Ic } from '../icons';
import { Eyebrow } from '../ui';
import useLiveBroadcast from '../live/useLiveBroadcast';
import BroadcastControls from '../live/BroadcastControls';
import { streamSources } from '../live/sources';
import '../live/live.css';

const COPY = {
  ru: {
    eyebrow: 'OO EkoTime проект Федерация Киберспорта СКО', title: 'ЭФИР ФЕДЕРАЦИИ',
    intro: 'Турнир на двух площадках. Выберите трансляцию или смотрите обе одновременно.',
    both: 'Два эфира', mode: 'Режим просмотра', full: 'На весь экран', exit: 'Свернуть',
    sound: 'Включите звук у нужного эфира в самом плеере.',
    first: 'Площадка 01', second: 'Площадка 02', channel: 'Официальный канал',
    waiting: 'ГОТОВИМСЯ К ЭФИРУ', waitingText: 'Трансляция появится здесь после анонса. Страница обновится автоматически.',
    open: 'Открыть', reload: 'Обновить плеер', other: 'Смотреть здесь',
    help: 'Если видео недоступно, откройте трансляцию на самой площадке.',
    follow: 'Следите за турниром', draw: 'Жеребьёвка Dota 2', tours: 'Все турниры',
    offline: 'Нет связи с сайтом. Работающие плееры остаются доступны; новые ссылки появятся после подключения.',
    fullscreenHint: 'Для полного экрана используйте кнопку в плеере или меню браузера.',
  },
  kz: {
    eyebrow: 'OO EkoTime проект Федерация Киберспорта СКО', title: 'ФЕДЕРАЦИЯ ЭФИРІ',
    intro: 'Турнир екі платформада. Бір трансляцияны таңдаңыз немесе екеуін қатар көріңіз.',
    both: 'Екі эфир', mode: 'Көру режимі', full: 'Толық экран', exit: 'Кішірейту',
    sound: 'Қажетті эфирдің дыбысын ойнатқышта қосыңыз.',
    first: 'Алаң 01', second: 'Алаң 02', channel: 'Ресми арна',
    waiting: 'ЭФИРГЕ ДАЙЫНДЫҚ', waitingText: 'Трансляция хабарландырудан кейін осында пайда болады. Бет автоматты түрде жаңарады.',
    open: 'Ашу', reload: 'Ойнатқышты жаңарту', other: 'Осында көру',
    help: 'Бейне қолжетімсіз болса, трансляцияны платформаның өзінде ашыңыз.',
    follow: 'Турнирді бақылаңыз', draw: 'Dota 2 жеребесі', tours: 'Барлық турнирлер',
    offline: 'Сайтпен байланыс үзілді. Ойнатқыштар жұмысын жалғастырады; жаңа сілтемелер байланыс қалпына келгенде пайда болады.',
    fullscreenHint: 'Толық экран үшін ойнатқыштағы түймені немесе браузер мәзірін қолданыңыз.',
  },
};

function StreamCard({ stream, T, single, select }) {
  const [reload, setReload] = useState(0);
  const host = useRef(null);
  const [width, setWidth] = useState(400);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width || 400));
    if (host.current) observer.observe(host.current);
    return () => observer.disconnect();
  }, []);
  // Twitch requires a 400×300 logical viewport, including on narrow phones.
  const frameWidth = Math.max(400, width);
  const frameHeight = Math.max(300, frameWidth * 9 / 16);
  const number = stream.id === 'twitch' ? T.first : T.second;
  return <article className={`fed-stream-card is-${stream.id}`} aria-label={`${stream.name} — ${number}`}>
    <header><div className="fed-stream-brand"><span className="fed-stream-icon"><Ic name={stream.id} size={24} /></span><div><span className="fed-stream-number">{number}</span><h2>{stream.name}</h2></div></div>
      {!single && <button className="fed-stream-focus" onClick={() => select(stream.id)} aria-label={`${T.other}: ${stream.name}`}><Ic name="external" size={17} /></button>}
      <span className="fed-stream-source">{stream.id === 'twitch' ? `@${stream.source || 'cybersqo'}` : T.channel}</span>
    </header>
    <div className="fed-stream-viewport" ref={host} style={{ height: frameHeight * width / frameWidth }}>
      {stream.embed ? <iframe key={`${stream.source}-${reload}`} src={stream.embed} title={`${stream.name} — ${number}`} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" style={{ width: frameWidth, height: frameHeight, transform: `scale(${width / frameWidth})` }} />
        : <div className="fed-stream-waiting"><span className="fed-stream-waiting-icon"><Ic name={stream.id} size={48} /></span><h3>{T.waiting}</h3><p>{T.waitingText}</p>{stream.url && <a href={stream.url} target="_blank" rel="noopener noreferrer">{T.open} {stream.name}<Ic name="external" size={14} /></a>}</div>}
    </div>
    <footer><a href={stream.url || '#'} target="_blank" rel="noopener noreferrer">{T.open} {stream.name}<Ic name="external" size={14} /></a>{stream.embed && <button onClick={() => setReload((n) => n + 1)} aria-label={`${T.reload}: ${stream.name}`}>{T.reload}</button>}</footer>
  </article>;
}

export default function FedLive() {
  const lang = useFedStore((state) => state.lang);
  const user = useAppStore((state) => state.user);
  const broadcast = useLiveBroadcast();
  const T = COPY[lang === 'kz' ? 'kz' : 'ru'];
  const [mode, setMode] = useState(() => window.matchMedia('(max-width: 899px)').matches ? 'twitch' : 'both');
  const [isFull, setFull] = useState(false);
  const [fullHint, setFullHint] = useState(false);
  const screen = useRef(null);
  const sources = streamSources(broadcast.live, window.location.hostname, window.location.origin);
  const editor = ['admin', 'smm'].includes(user?.user_type);
  useEffect(() => {
    const change = () => setFull(document.fullscreenElement === screen.current);
    document.addEventListener('fullscreenchange', change);
    return () => document.removeEventListener('fullscreenchange', change);
  }, []);
  const fullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (screen.current.requestFullscreen) await screen.current.requestFullscreen();
      else setFullHint(true);
    } catch { setFullHint(true); }
  };
  return <div className="fed-pagewrap fed-live-page">
    <header className="fed-live-heading"><div><Eyebrow>{T.eyebrow}</Eyebrow><h1>{T.title}<span>.</span></h1><p>{T.intro}</p></div><div className="fed-live-platforms" aria-hidden="true"><Ic name="twitch" size={22} /><span>×</span><Ic name="youtube" size={25} /></div></header>
    {editor && <BroadcastControls {...broadcast} settingsHref={`/#${user.user_type === 'smm' ? 'smm' : 'admin'}?section=live`} />}
    <section ref={screen} className={`fed-live-screen mode-${mode}`} aria-label={T.mode}>
      <div className="fed-live-toolbar"><div className="fed-live-modes" role="group" aria-label={T.mode}>{[['both', T.both, 'grid'], ['twitch', 'Twitch', 'twitch'], ['youtube', 'YouTube', 'youtube']].map(([id, label, icon]) => <button key={id} aria-pressed={mode === id} onClick={() => setMode(id)}><Ic name={icon} size={16} />{label}</button>)}</div><button className="fed-live-fullscreen" onClick={fullscreen}><Ic name="external" size={16} /><span>{isFull ? T.exit : T.full}</span></button></div>
      <div className="fed-live-streams">{sources.filter((source) => mode === 'both' || mode === source.id).map((source) => <StreamCard key={source.id} stream={source} T={T} single={mode !== 'both'} select={setMode} />)}</div>
      <div className="fed-live-player-note"><span>{T.sound}</span><span>{T.help}</span></div>
      {fullHint && <p role="status" className="fed-live-player-note">{T.fullscreenHint}</p>}
    </section>
    {!broadcast.online && broadcast.data && <p className="fed-live-connection" role="status">{T.offline}</p>}
    <footer className="fed-live-links"><span>{T.follow}</span><a href="/jerebiovka"><Ic name="grid" size={17} />{T.draw}<Ic name="chevR" size={16} /></a><a href="/#tournaments">{T.tours}<Ic name="chevR" size={16} /></a></footer>
  </div>;
}
